/**
 * Session guard for manual Mixpanel experiment exposures (`$experiment_started`).
 *
 * Exposure call sites can run more than once per session (screen re-mounts,
 * shared hooks mounted on several screens).
 *
 * This module remembers which user, experiment and variant
 * combinations have already been reported so the provider can send each
 * exposure a single time. The guard lives for the app process and is cleared
 * when telemetry is reset on logout.
 */

// ─────────────────────────────────────────────────────────────────────────────

export function checkIfExperimentExposureTracked(params: Params) {
  return trackedExperimentExposures.has(getExperimentExposureKey(params));
}

export function markExperimentExposureTracked(params: Params) {
  trackedExperimentExposures.add(getExperimentExposureKey(params));
}

export function resetExperimentExposures() {
  trackedExperimentExposures.clear();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getExperimentExposureKey(params: Params) {
  const { userId = ANONYMOUS_USER_ID, experimentName, variantName } = params;

  return [userId, experimentName, variantName].join(EXPERIMENT_EXPOSURE_KEY_SEPARATOR);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ANONYMOUS_USER_ID = 'anonymous';
const EXPERIMENT_EXPOSURE_KEY_SEPARATOR = '|';

/** Exposures already sent during this app session, keyed by user, experiment and variant. */
const trackedExperimentExposures = new Set<string>();

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  userId: string | undefined;
  experimentName: string;
  variantName: string;
};
